import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Form, Input, Select, Space, Typography, } from 'antd';
import { useTrackingCode } from "react-hubspot-tracking-code-hook";

import styles from './form-styles.module.scss';
import { StepFormProps } from '../types/types';
import { useShared } from '../context/shared.context';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const { Title, Text } = Typography;

export const SummaryInformationForm: React.FC<StepFormProps> = ( props ) => {

  const { setTrackPageView } = useTrackingCode();
  setTrackPageView();

  const { onNext, defaultValues } = props;
  const { listCountries, listJobTitles } = useShared();

  const [notValidPhone, setNotValidPhone] = useState<boolean>(false);
  const [prefix, setPrefix] = useState<any>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(defaultValues);
    window?.scrollTo?.(0, 0);
    window?.dataLayer?.push({
      event: 'pageview',
      page: {
        url: `${'/contact-information'}${window.location.search}`,
        title: 'Contact Information',
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function getLabel( label: string ) {
    return <div><Text type="danger" strong>*</Text>{' '}<Text strong>{label}</Text></div>;
  }

  function validatePhone() {
    form
      .validateFields(['phone'])
      .then(( values ) => {
        setNotValidPhone(false);
      })
      .catch(( info ) => {
        info.errorFields.length > 0 ? setNotValidPhone(true) : setNotValidPhone(false);
      });
  }

  return (

    <Space direction={'vertical'} size={'large'} className={'full-width'}>
      <Space direction={'vertical'} size={'small'}>
        <Title
          style={{ margin: 0 }}
          level={2}
          data-cy="app-page-title"
        >
          About You
        </Title>
        <Text type={'secondary'}>
          First, tell us a little bit about yourself and how we can contact you.
        </Text>
      </Space>

      <Form
        requiredMark={false}
        name="contact-information-form-step-1"
        onFinish={async ( data ) => {
          onNext?.(data);
        }}
        autoComplete="off"
        form={form}
        layout={'vertical'}
        className={styles['sections-wrapper']}
      >
        <Space direction={'vertical'} size={'large'} className={'full-width'}>

          <Space direction={'vertical'} size={'small'} className={'full-width'}>
            <Form.Item
              label={getLabel("Work email address")}
              name={['email']}
              validateTrigger={['onBlur', 'onSubmit']}
              rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input
                size="large"
                placeholder={'example@company.com'}
                data-cy="contact-information-email-input"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              label={getLabel("First name")}
              name={['firstname']}
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Input
                size="large"
                placeholder={'First name'}
                data-cy="contact-information-first-name-input"
              />
            </Form.Item>

            <Form.Item
              label={getLabel("Surname")}
              name={['surname']}
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Input
                size="large"
                placeholder={'Surname'}
                data-cy="contact-information-surname-input"
              />
            </Form.Item>

            <Form.Item
              label={getLabel("Phone number")}
              name={['phone']}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                }
              ]}
              className={notValidPhone ? styles['phone-input-error'] : styles['phone-input']}
            >
              <PhoneInput
                preferredCountries={['ie', 'gb']}
                regions={'europe'}
                value={prefix}
                country={'ie'}
                placeholder={'Phone number'}
                onChange={( phone ) => {
                  validatePhone();
                  setPrefix(phone);
                }}
              />
            </Form.Item>

            <Form.Item
              label={getLabel("Job title")}
              name={['jobTitle']}
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Select
                size="large"
                placeholder="Select an option"
                data-cy="contact-information-job-title-input"
              >
                {(listJobTitles?.result || []).map(( job: any ) => (
                  <Select.Option
                    key={job.key}
                    value={job.key}
                    data-cy="contact-information-title-select-options"
                  >
                    {job.text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={getLabel("Where is your company registered?")}
              name={['countryOfIncorporation']}
              rules={[
                { required: true, message: 'This field is required' },
              ]}
            >
              <Select
                size="large"
                placeholder="Select country"
                data-cy="contact-information-country-select"
                optionFilterProp="children"
              >
                {(listCountries?.result || []).map(( country: any ) => (
                  <Select.Option key={country.code} value={country.code}>
                    {country.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={getLabel("Registered company name")}
              name={['companyName']}
              validateTrigger={['onBlur', 'onSubmit']}
              rules={[{ required: true, message: 'This field is required' },
                { min: 3, message: 'The company name should contain at least 3 characters' }
              ]}
            >
              <Input
                size="large"
                placeholder={'Company name'}
                data-cy="contact-information-companyName-input"
              />
            </Form.Item>

            <Form.Item
              name={['termAndCondition']}
              valuePropName="checked"
              rules={[
                {
                  validator: ( _, checked ) =>
                    checked
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please accept the Terms and Conditions and Privacy Policy')),
                },
              ]}
            >
              <Checkbox>
                I have read and accepted the {' '}
                <a target="_blank" href="https://financefair.com/terms-and-conditions" rel="noopener noreferrer">
                  <Typography.Text strong>
                    Terms and Conditions
                  </Typography.Text>
                </a>{' '}
                and{' '}
                <a target="_blank" href="https://financefair.com/privacy-policy/" rel="noopener noreferrer">
                  <Typography.Text strong>
                    Privacy Policy
                  </Typography.Text>
                </a>
                .
              </Checkbox>
            </Form.Item>
            <Form.Item
              label='amex'
              name='amex'
              style={{ display: 'none', rowGap: "0px" }}
            >
              <Input
                size="large"
                placeholder='amex'
              />
            </Form.Item>
          </Space>
          <Button
            block
            type="primary"
            htmlType="submit"
            onClick={() => {
              validatePhone();
            }}
            data-cy="app-page-next-button">
            Continue
          </Button>
        </Space>
      </Form>

    </Space>
  );
};
